import React, { useRef } from 'react';
import { Button, Card, Progress, Space, Tag, Typography } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { PicqerLocation, PicqerWarehouse } from './types';
import { PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY } from './types';

const { Text } = Typography;

interface WarehousePanelProps {
  warehouse: PicqerWarehouse;
  locations: PicqerLocation[];
  /** Parent/grouping locations excluded from analysis (e.g. C, C.1) */
  parentLocationCount?: number;
  locationsLoading: boolean;
  locationProgress: { loaded: number; page: number } | null;
  historyLoaded: number;
  historyTotal: number;
  /** All locations history is fetched for (incl. parent nodes probed for stock) */
  historyFetchTotal?: number;
  historyFetchLoaded?: number;
  historyLoading: boolean;
  currentLocationId: number | null;
}

export const WarehousePanel: React.FC<WarehousePanelProps> = ({
  warehouse,
  locations,
  parentLocationCount = 0,
  locationsLoading,
  locationProgress,
  historyLoaded,
  historyTotal,
  historyFetchTotal,
  historyFetchLoaded,
  historyLoading,
  currentLocationId,
}) => {
  const fetchTotal = historyFetchTotal ?? historyTotal;
  const fetchLoaded = historyFetchLoaded ?? historyLoaded;
  const historyPercent = fetchTotal > 0 ? Math.round((fetchLoaded / fetchTotal) * 100) : 0;

  return (
    <Card
      title={warehouse.name}
      size="small"
      extra={
        <Space>
          <Tag color={warehouse.active ? 'green' : 'default'}>
            {warehouse.active ? 'Active' : 'Inactive'}
          </Tag>
          <Text type="secondary">ID {warehouse.idwarehouse}</Text>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <Text strong>Real locations: </Text>
          <Text>{locations.length > 0 ? locations.length : '—'}</Text>
          {parentLocationCount > 0 && (
            <Text type="secondary"> ({parentLocationCount} parent nodes excluded)</Text>
          )}
          {locationsLoading && (
            <Text type="secondary">
              {locationProgress
                ? ` (loading page ${locationProgress.page}, ${locationProgress.loaded} so far…)`
                : ' (loading…)'}
            </Text>
          )}
        </div>
        {historyTotal > 0 && (
          <div>
            <Text strong>Stock history: </Text>
            <Text>
              {historyLoaded} / {historyTotal} analysable
            </Text>
            {historyFetchTotal != null && historyFetchTotal > historyTotal && (
              <Text type="secondary"> ({historyFetchLoaded ?? 0} / {historyFetchTotal} fetched)</Text>
            )}
            {historyLoading && currentLocationId != null && (
              <Text type="secondary"> — location #{currentLocationId}</Text>
            )}
            <Progress percent={historyPercent} size="small" style={{ marginTop: 8 }} />
          </div>
        )}
      </Space>
    </Card>
  );
};

interface FetchActionsProps {
  onFreshFetch: () => void;
  onFetchWarehouses: () => void;
  onLoadLocations: () => void;
  onLoadHistory: () => void;
  onCancel: () => void;
  fetchPhase: FetchPhase;
  fetchStatusText: string | null;
  warehousesLoading: boolean;
  locationsLoading: boolean;
  historyLoading: boolean;
  isFetching: boolean;
  hasWarehouses: boolean;
  hasLocations: boolean;
  apiKeyPoolSize: number;
  historyConcurrency: number;
}

export type FetchPhase = 'idle' | 'warehouses' | 'locations' | 'history' | 'done';

export const FetchActions: React.FC<FetchActionsProps> = ({
  onFreshFetch,
  onFetchWarehouses,
  onLoadLocations,
  onLoadHistory,
  onCancel,
  fetchPhase,
  fetchStatusText,
  warehousesLoading,
  locationsLoading,
  historyLoading,
  isFetching,
  hasWarehouses,
  hasLocations,
  apiKeyPoolSize,
  historyConcurrency,
}) => (
  <Card title="Fetch from Picqer" size="small" style={{ marginBottom: 16 }}>
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap>
        <Button type="primary" loading={isFetching} onClick={onFreshFetch}>
          Fresh fetch (warehouses → locations → history)
        </Button>
        {isFetching && (
          <Button danger onClick={onCancel}>
            Cancel
          </Button>
        )}
      </Space>
      {fetchStatusText && (
        <Text type="secondary">{fetchStatusText}</Text>
      )}
      <Text type="secondary" style={{ fontSize: 12 }}>
        Step 3 uses {historyConcurrency} workers across {apiKeyPoolSize} API key{apiKeyPoolSize !== 1 ? 's' : ''} (≤{PICQER_MAX_REQUESTS_PER_MINUTE_PER_KEY} req/min each):
      </Text>
      <Space wrap>
        <Button loading={warehousesLoading} disabled={isFetching} onClick={onFetchWarehouses}>
          {hasWarehouses ? '1. Refresh warehouses' : '1. Fetch warehouses'}
        </Button>
        <Button
          loading={locationsLoading}
          disabled={!hasWarehouses || isFetching}
          onClick={onLoadLocations}
        >
          2. Load all locations
        </Button>
        <Button
          loading={historyLoading}
          disabled={!hasLocations || isFetching}
          onClick={onLoadHistory}
        >
          3. Load stock history
        </Button>
      </Space>
      {fetchPhase !== 'idle' && fetchPhase !== 'done' && (
        <Text type="secondary">
          Step: {fetchPhase === 'warehouses' ? '1/3 Warehouses' : fetchPhase === 'locations' ? '2/3 Locations' : '3/3 Stock history'}
        </Text>
      )}
    </Space>
  </Card>
);

interface DataPersistenceActionsProps {
  onImport: (file: File) => void;
  onExportJson: () => void;
  onExportCsv: () => void;
  onClearData: () => void;
  importLoading: boolean;
  canExportJson: boolean;
  canExportCsv: boolean;
  hasData: boolean;
  importStats: string | null;
}

export const DataPersistenceActions: React.FC<DataPersistenceActionsProps> = ({
  onImport,
  onExportJson,
  onExportCsv,
  onClearData,
  importLoading,
  canExportJson,
  canExportCsv,
  hasData,
  importStats,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Card title="Saved data" size="small" style={{ marginBottom: 16 }}>
      <Space wrap align="start">
        <input
          ref={fileInputRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImport(file);
            e.target.value = '';
          }}
        />
        <Button
          icon={<UploadOutlined />}
          loading={importLoading}
          onClick={() => fileInputRef.current?.click()}
        >
          Import JSON
        </Button>
        <Button icon={<DownloadOutlined />} disabled={!canExportJson} onClick={onExportJson}>
          Export JSON
        </Button>
        <Button disabled={!canExportCsv} onClick={onExportCsv}>
          Export metrics CSV
        </Button>
        {hasData && (
          <Button danger type="text" onClick={onClearData}>
            Clear loaded data
          </Button>
        )}
      </Space>
      {importStats && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary">{importStats}</Text>
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        <Text type="secondary">
          Export JSON after step 1 (warehouses) or step 2 (locations) — import later and continue with step 3.
          Metrics CSV requires stock history.
        </Text>
      </div>
    </Card>
  );
};
